// MPIN and UPI PIN pads (spec §9.1, §9.2). Six dots, a 3×4 keypad, auto-submit
// at the configured PIN length. The sheet can't be dismissed by tapping outside; the
// close button cancels. The UPI variant is deliberately styled differently
// (green accent, UPI badge, square dots) so the customer knows which secret
// they are typing.
import { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDeleteLeft } from '@fortawesome/free-solid-svg-icons';
import './auth.css';

export interface PinPadProps {
  show: boolean;
  variant: 'MPIN' | 'UPI';
  title: string;
  subtitle?: string;
  error?: string;
  busy?: boolean;
  /** Bump to clear the entered digits (e.g. after a wrong PIN). */
  resetKey?: number;
  /** Supported server PIN lengths are four or six digits. */
  pinLength?: 4 | 6;
  allowLengthSelection?: boolean;
  onPinLengthChange?: (length: 4 | 6) => void;
  onComplete: (pin: string) => void;
  onClose: () => void;
}

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', 'back'];

const PinPad = ({
  show,
  variant,
  title,
  subtitle,
  error,
  busy = false,
  resetKey = 0,
  pinLength = 6,
  allowLengthSelection = false,
  onPinLengthChange,
  onComplete,
  onClose,
}: PinPadProps) => {
  const [pin, setPin] = useState('');

  useEffect(() => {
    if (show) setPin('');
  }, [show, resetKey]);

  const press = (key: string) => {
    if (busy) return;
    if (key === 'back') {
      setPin((p) => p.slice(0, -1));
      return;
    }
    if (!key || pin.length >= pinLength) return;
    const next = pin + key;
    setPin(next);
    if (next.length === pinLength) {
      // Submit automatically at the configured PIN length.
      setTimeout(() => onComplete(next), 120);
    }
  };

  const isUpi = variant === 'UPI';

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      backdrop="static"
      keyboard={false}
      className={`nova-auth-modal ${isUpi ? 'nova-auth-upi' : ''}`}
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {isUpi && <span className="nova-upi-badge">UPI</span>} {title}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="text-center">
        {subtitle && <p className="nova-auth-approval">{subtitle}</p>}
        {allowLengthSelection && (
          <div className="btn-group mb-3" role="group" aria-label="PIN length">
            {[4, 6].map((length) => (
              <button
                key={length}
                type="button"
                className={`btn btn-sm ${pinLength === length ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => {
                  onPinLengthChange?.(length as 4 | 6);
                  setPin('');
                }}
                disabled={busy}
              >
                {length} digits
              </button>
            ))}
          </div>
        )}
        <div className={`nova-pin-dots ${isUpi ? 'nova-pin-dots-square' : ''}`} aria-label={`${pin.length} of ${pinLength} digits entered`}>
          {Array.from({ length: pinLength }).map((_, i) => (
            <span key={i} className={`nova-pin-dot ${i < pin.length ? 'filled' : ''}`} />
          ))}
        </div>
        {error && <p className="nova-auth-error">{error}</p>}
        <div className="nova-pin-keypad">
          {KEYS.map((key, i) =>
            key === '' ? (
              <span key={`blank-${i}`} className="nova-pin-key nova-pin-key-blank" />
            ) : (
              <button
                key={key}
                type="button"
                className="nova-pin-key"
                disabled={busy}
                onClick={() => press(key)}
                aria-label={key === 'back' ? 'Backspace' : `Digit ${key}`}
              >
                {key === 'back' ? <FontAwesomeIcon icon={faDeleteLeft} /> : key}
              </button>
            )
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default PinPad;
